import { CompletionsOverTimeReport } from '@/client/dashboard/types';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import {
  calculateLineChartYAxisMax,
  createCustomTooltip,
  createLineChartGradient,
  getChartTranslations,
  getCompletionsDataMappedToLabels,
  hideTooltipForNavigation,
} from '@/ui/charts/charts.helper';
import { getKPITimeBasedLabels } from '@/utils/helpers';
import {
  colorBlackMain,
  colorBlueDark,
  colorGrayDark,
  platformFontFamily,
} from '@/utils/helpers/css-variables.helper';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
} from 'chart.js';
import { Chart } from 'primereact/chart';
import { Skeleton } from 'primereact/skeleton';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  Title,
  Tooltip,
  Legend,
);

const ChartWrapper = styled.div`
  height: 100%;
  width: 100%;
  flex: 1;
`;

const ChartContainer = styled.div`
  overflow-x: scroll;
  overflow-y: visible;
  width: 100%;
  height: auto;
  min-height: auto;
  position: relative;
  background: var(--white-main);
  margin-top: var(--default-padding);
`;

const StyledBarChart = styled(Chart)`
  width: 100%;
  height: 100%;
`;

type CompletionsLineChartProps = {
  id?: string;
  fromDate?: Date | undefined;
  toDate?: Date | undefined;
  reportData?: CompletionsOverTimeReport[];
  isLoading?: boolean;
};

export const CompletionsLineChart = ({
  id,
  fromDate,
  toDate,
  reportData,
  isLoading,
}: CompletionsLineChartProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const chartRef = useRef<Chart>(null);
  const [chartData, setChartData] = useState({});
  const [chartOptions, setChartOptions] = useState({});
  const [datasets, setDatasets] = useState<
    {
      label: string;
      value: CompletionsOverTimeReport | null;
    }[]
  >([]);
  const [isChartDataLoading, setIsChartDataLoading] = useState(false);
  const docStyle = getComputedStyle(document.documentElement);
  const textColorSecondary = colorBlackMain(docStyle);

  useEffect(() => {
    if (!reportData) return;
    const vulnerableUsersOverviewLabels = getKPITimeBasedLabels(
      fromDate,
      toDate,
      reportData,
      t,
    );

    const datasetsData = getCompletionsDataMappedToLabels(
      reportData,
      vulnerableUsersOverviewLabels,
    );

    setIsChartDataLoading(true);
    setDatasets(datasetsData);
  }, [reportData]);

  useEffect(() => {
    const destroyChart = () => {
      if (chartRef.current) {
        const chartInstance = (chartRef.current as any).chart;
        if (chartInstance) {
          chartInstance.destroy();
        }
      }
    };
    destroyChart();

    const getCompletionsRate = (item: CompletionsOverTimeReport | null) => {
      if (item?.cumulativeEnrollments && item?.cumulativeCompleted) {
        return Number(
          (
            (item.cumulativeCompleted / item.cumulativeEnrollments) *
            100
          ).toFixed(2),
        );
      }
      return null;
    };

    const gradient = createLineChartGradient(true, docStyle);
    const mainColor = colorBlueDark(docStyle);
    const chartTranslations = getChartTranslations(true, t);

    const chartDatasets = [
      {
        type: 'line',
        label: chartTranslations.mainLabel,
        backgroundColor: gradient || mainColor,
        borderColor: mainColor,
        borderWidth: 0,
        data: datasets.map((item) => getCompletionsRate(item.value)),
        fill: true,
        tension: 0,
        pointRadius: 5,
        pointHoverRadius: 7,
        pointBackgroundColor: mainColor,
        pointBorderColor: mainColor,
        pointStyle: 'rectRounded',
        pointBorderWidth: 0,
        clip: false as const,
      },
    ];

    // if only one result -> center the chart
    const isSingleDataPoint = datasets.length === 1;

    const labels = isSingleDataPoint
      ? ['', datasets[0]?.label || '', '']
      : datasets?.map((item) => item.label);

    const centeredChartData = isSingleDataPoint
      ? [null, chartDatasets[0].data[0], null]
      : chartDatasets[0].data;

    const centeredDatasets = [
      {
        ...chartDatasets[0],
        data: centeredChartData,
      },
    ];

    const data = {
      labels,
      datasets: centeredDatasets,
    };

    const options = {
      maintainAspectRatio: false,
      aspectRatio: 0.6,
      interaction: {
        mode: 'index' as const,
        intersect: false,
      },
      plugins: {
        tooltip: {
          enabled: false,
          external: (context: any) =>
            createCustomTooltip(context, t, datasets, false, true, true),
        },
        legend: {
          display: false,
        },
        datalabels: {
          display: false,
        },
      },
      scales: {
        x: {
          ticks: {
            color: textColorSecondary,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
          },
          barPercentage: 0.5,
          categoryPercentage: 0.6,
          border: {
            dash: [5, 5],
            display: false,
          },
        },
        y: {
          min: 0,
          max: calculateLineChartYAxisMax(datasets, true),
          ticks: {
            color: textColorSecondary,
            stepSize: 25,
            callback: (value: number) => `${value}%`,
            font: {
              family: platformFontFamily(docStyle),
            },
          },
          grid: {
            display: true,
            color: colorGrayDark(docStyle),
            lineWidth: 1,
            dash: [10, 10],
          },
          border: {
            dash: [5, 5],
            display: false,
          },
        },
      },
      elements: {
        bar: {
          borderRadius: {
            topLeft: 5,
            topRight: 5,
          },
        },
        line: {
          borderDash: [7, 5],
        },
      },
      onHover: (event: any, activeElements: any[]) => {
        if (!event?.native?.target?.style) return;
        event.native.target.style.cursor =
          activeElements.length > 0 ? 'pointer' : 'default';
      },
      onClick: (event: any, activeElements: any[], chart: any) => {
        if (!activeElements || !chart?.canvas) return;

        if (activeElements.length > 0) {
          const tooltipId = `chart-tooltip-${chart.canvas.id || 'default'}`;
          hideTooltipForNavigation(tooltipId);
          navigate(RedirectPaths[RedirectPathsEnum.REPORTS_AWARENESS]());
        }
      },
    };

    setChartData(data);
    setChartOptions(options);
    setIsChartDataLoading(false);

    return () => {
      destroyChart();
    };
  }, [datasets]);

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        const chartInstance = (chartRef.current as any).chart;
        if (chartInstance) {
          chartInstance.destroy();
        }
      }
      const tooltipId = `chart-tooltip-${id}-line-chart`;
      hideTooltipForNavigation(tooltipId);
    };
  }, [id]);

  return (
    <ChartContainer id={id}>
      <ChartWrapper>
        {isLoading || isChartDataLoading ? (
          <Skeleton height="100%" width="100%" />
        ) : (
          <StyledBarChart
            ref={chartRef}
            type="line"
            data={chartData}
            options={chartOptions}
            id={`${id}-line-chart`}
          />
        )}
      </ChartWrapper>
    </ChartContainer>
  );
};
